<template>
  <div
    class="v3-info-box"
    :role="containerRole"
    :aria-live="containerAriaLive"
    :aria-atomic="containerAriaAtomic"
    :aria-relevant="containerAriaRelevant"
    :aria-labelledby="titleHeadingId || undefined"
  >
    <h2 v-if="title" :id="titleHeadingId" class="v3-info-box-title">
      {{ title }}
    </h2>
    <div class="v3-info-box-content">
      <p v-if="message" class="v3-info-box-content-message">{{ message }}</p>
      <slot></slot>
      <slot name="slot"></slot>
    </div>
  </div>
</template>

<script>
import { computed, useAttrs, watch } from "vue"
import { announce } from "@/composables/useLiveAnnouncer"

let infoBoxHeadingSeq = 0

export default {
  inheritAttrs: true,
  props: {
    title: {
      type: String,
      default: ""
    },
    message: {
      type: String,
      default: ""
    },
    /**
     * When false, box is a static `role="region"` (no polite live announcement).
     * Default true preserves previous status-region behavior.
     */
    useLiveRegion: {
      type: Boolean,
      default: true
    },
    /**
     * Use `role="alert"` + assertive live region (e.g. form success/error feedback
     * that must be announced reliably across SRs).
     */
    announceAssertive: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const attrs = useAttrs()
    const headingInstance = ++infoBoxHeadingSeq
    const titleHeadingId = computed(() =>
      props.title ? `v3-info-box-title-${headingInstance}` : ""
    )

    const isDangerVariant = computed(() => {
      const classBinding = attrs.class
      if (!classBinding) return false

      if (typeof classBinding === "string") {
        return classBinding.split(" ").includes("danger")
      }

      if (Array.isArray(classBinding)) {
        return classBinding
          .flatMap((item) => {
            if (typeof item === "string") return item.split(" ")
            if (item && typeof item === "object") {
              return Object.keys(item).filter((key) => item[key])
            }
            return []
          })
          .includes("danger")
      }

      if (typeof classBinding === "object" && classBinding !== null) {
        return Boolean(classBinding.danger)
      }

      return false
    })

    const containerRole = computed(() => {
      if (!props.useLiveRegion) return "region"
      if (props.announceAssertive) return "alert"
      return isDangerVariant.value ? "alert" : "status"
    })

    const containerAriaLive = computed(() => {
      if (!props.useLiveRegion) return undefined
      if (props.announceAssertive) return "assertive"
      return isDangerVariant.value ? "assertive" : "polite"
    })

    const containerAriaAtomic = computed(() => {
      if (!props.useLiveRegion) return undefined
      return "true"
    })

    const containerAriaRelevant = computed(() => {
      if (!props.useLiveRegion) return undefined
      return "additions text"
    })

    /* The box is often mounted together with its text (v-if), so the in-place live
       region is not reliably announced. Mirror the title + message into the persistent
       global live region (axe: status-msg-not-auto-announced) so e.g. "Success" status
       context is conveyed along with the detail ("Success. Successfully saved!"). */
    watch(
      () => [props.title, props.message],
      ([title, message]) => {
        if (!props.useLiveRegion || !message?.trim?.()) return
        const announcement = [title?.trim?.(), message.trim()]
          .filter(Boolean)
          .join(". ")
        announce(announcement, {
          assertive: props.announceAssertive || isDangerVariant.value
        })
      },
      { immediate: true }
    )

    return {
      titleHeadingId,
      containerRole,
      containerAriaLive,
      containerAriaAtomic,
      containerAriaRelevant
    }
  }
}
</script>
